import { useState } from 'react';
import { Popover } from 'react-tiny-popover';
import styled from 'styled-components';
import DefaultSortIcon from '../../../assets/icons/sort-default.svg?react';
import CloseIcon from '../../../assets/icons/close.svg?react';
import AscIcon from '../../../assets/icons/sort_asc.svg?react';
import DescIcon from '../../../assets/icons/sort_desc.svg?react';

interface SortButtonProps {
  onClick: (sortKey: string | null) => void;
  sortKey: string | null;
  idPrefix?: string;
}

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.8048 4.19526C14.0652 4.45561 14.0652 4.87772 13.8048 5.13807L7.13815 11.8047C6.8778 12.0651 6.45569 12.0651 6.19534 11.8047L2.86201 8.4714C2.60166 8.21106 2.60166 7.78894 2.86201 7.5286C3.12236 7.26825 3.54447 7.26825 3.80482 7.5286L6.66675 10.3905L12.862 4.19526C13.1224 3.93491 13.5445 3.93491 13.8048 4.19526Z"
      fill="#228BE6"
    />
  </svg>
);

const sortTypes = [
  {
    label: 'Ascending',
    value: 'asc',
    icon: <AscIcon />
  },
  {
    label: 'Descending',
    value: 'desc',
    icon: <DescIcon />
  }
];

export default function SortButton({ onClick, sortKey, idPrefix = 'table-sort' }: SortButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedType = sortTypes.find(el => el.value === sortKey);

  return (
    <Popover
      isOpen={isOpen}
      onClickOutside={() => setIsOpen(false)}
      positions={['bottom']}
      padding={5}
      content={
        <Container id={`${idPrefix}-menu`}>
          <Menu id={`${idPrefix}-menu-list`}>
            {sortTypes.map((type, idx) => (
              <Item id={`${idPrefix}-menu-item-${type.value}`} key={idx + '_' + type.value}>
                <button
                  id={`${idPrefix}-menu-button-${type.value}`}
                  onClick={() => onClick(type.value)}
                  //@ts-ignore
                  className={sortKey === type.value && 'selected'}
                >
                  {type.icon}
                  <span>{type.label}</span>
                  {sortKey === type.value && (
                    <span className="check-icon">
                      <CheckIcon />
                    </span>
                  )}
                </button>
              </Item>
            ))}
          </Menu>

          <Divider id={`${idPrefix}-menu-divider`} />

          <Menu id={`${idPrefix}-menu-clear-list`}>
            <Item id={`${idPrefix}-menu-clear-item`}>
              <button id={`${idPrefix}-menu-clear-button`} onClick={() => onClick(null)}>
                <CloseIcon />
                Clear Sorting
              </button>
            </Item>
          </Menu>
        </Container>
      }
    >
      <Button
        id={`${idPrefix}-trigger`}
        onClick={() => setIsOpen(prev => !prev)}
        style={{ marginLeft: 8 }}
      >
        {selectedType?.icon || <DefaultSortIcon className="default-icon" />}
      </Button>
    </Popover>
  );
}

const Button = styled.button`
  .default-icon {
    path {
      fill: #adb5bd;
    }
  }

  svg {
    path {
      fill: #228be6;
    }
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  border: 1px solid #ced4da;
  background: #fff;
  padding: 8px;
  gap: 16px;

  /* xs */
  box-shadow:
    0px 1px 2px 0px rgba(0, 0, 0, 0.1),
    0px 1px 3px 0px rgba(0, 0, 0, 0.05);
`;

const Menu = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Item = styled.li`
  width: 178px;
  margin: 0;
  padding: 0;

  button {
    position: relative;
    width: 100%;
    padding: 8px;
    border-radius: 4px;
    display: flex;
    gap: 4px;
    align-items: center;
    color: #000;

    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 140%; /* 19.6px */
    cursor: pointer;

    svg {
      width: 16px;
      height: 16px;

      path {
        fill: #adb5bd;
      }
    }

    &:hover,
    &.selected {
      background: #f1f3f5;
    }

    .check-icon {
      position: absolute;
      right: 8px;
      top: 8px;

      svg {
        path {
          fill: #228be6;
        }
      }
    }
  }
`;

const Divider = styled.span`
  width: 100%;
  height: 1px;
  background: #f1f3f5;
`;
